import styles from "./Filters.module.scss";
import { useOnClickOutside } from "@/frontend/shared/hooks/useOnClickOutside";
import { useEffect, useRef, useState } from "react";
import { Divider } from "@/frontend/shared/ui/Divider/Divider";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography";
import { ConditionRow } from "@/frontend/components/FilterV2/ui/ConditionRow/ConditionRow";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { Drawer2 } from "@/frontend/shared/ui/Drawer2";
import { ConditionRowFilter, ConditionRowOptions, ConditionType } from "@/frontend/components/FilterV2/model/types";
import { v4 } from "uuid";
import { SearchInput } from "@/frontend/shared/ui/SearchInput";
import { nonUItypes } from "@/frontend/components/FilterV2/const/const";
import { AiIcon } from "@/frontend/shared/assets/icons";
import { Tabs } from "@/frontend/components/Tabs/Tabs";
import { BasicFilters } from "@/frontend/components/FilterV2/ui/BasicFilters/BasicFilters";
import { useDebouncedValue } from "@/frontend/shared/hooks/useDebouncedValue";

type TFilterProps = {
  filters: ConditionRowFilter[];
  onSave: (filters: ConditionRowFilter[]) => void;
  onCancel: () => void;
  options?: ConditionRowOptions;
  prefixId?: string;
  wrap?: boolean;
  noSearch?: boolean;
  noControls?: boolean;
  onFiltersComplexityTypeChange?: (type: "basic" | "advanced") => void;
};

type TFiltersModalProps = TFilterProps & {
  isOpen: boolean;
  anchorEl?: HTMLElement | null;
  fullWidth?: boolean;
};

const FILTERS_LIMIT = 20;

function getPositionBasedOnAnchor(anchorEl: HTMLElement | null | undefined) {
  if (anchorEl) {
    const rect = anchorEl.getBoundingClientRect();
    return {
      top: rect.top + rect.height + 4,
      left: rect.left
    };
  }
  return {
    top: 0,
    left: 0
  };
}

function aggregateErrors(filters: ConditionRowFilter[]) {
  return filters.map((filter) => {
    switch (filter.type) {
      case "foundingYear":
        if (filter.value) {
          const v = Number(filter.value);
          if (isNaN(v) || v < 1800 || v > 2100) filter.error = "Year must be between 1800 and 2100";
        }
        break;
    }
    return filter;
  });
}

const canSave = (filters: ConditionRowFilter[]) => filters.every((f) => !f.error && !!f.value);

const advancedFilters: ConditionType[] = [
  "foundingYear",
  "tag",
  "funding",
  "foundingYear",
  "lastFundingDate",
  "lastFundingYear"
];

const isAdvancedFilter = (f: ConditionRowFilter) =>
  (f.type !== "group" && f.type !== "description" && !f.isOr) ||
  advancedFilters.includes(f.type) ||
  (f.type === "group" && f.filters && f.filters.some(isAdvancedFilter));
const hasAdvancedFilters = (filters: ConditionRowFilter[]) => {
  return filters.some(isAdvancedFilter);
};

const extractSimpleFilters = (filters: ConditionRowFilter[]) =>
  filters
    .filter((f) => f.type === "group")
    .flatMap((f) => f.filters)
    .filter((f) => !!f && !isAdvancedFilter(f) && !nonUItypes.includes(f.type))
    .filter(Boolean) as ConditionRowFilter[];

export function Filters({
  options,
  filters,
  onSave: onSaveProp,
  onCancel: onCancelProp,
  prefixId = "",
  wrap = false,
  noSearch,
  noControls,
  onFiltersComplexityTypeChange
}: TFilterProps) {
  const [andFilters, setAndFilters] = useState<ConditionRowFilter[]>(
    filters.filter((filter) => !filter.isOr && !nonUItypes.includes(filter.type))
  );
  const [orFilters, setOrFilters] = useState<ConditionRowFilter[]>(
    filters.filter((filter) => filter.isOr && !nonUItypes.includes(filter.type))
  );

  const [simpleFilters, setSimpleFilters] = useState<ConditionRowFilter[]>(extractSimpleFilters(filters));

  const [searchValue, setSearchValue] = useState<string>(filters.find((f) => f.type === "description")?.value || "");
  const [useAdvancedFilters, setUseAdvancedFilters] = useState(hasAdvancedFilters(filters));

  const debouncedSearchValue = useDebouncedValue(searchValue, 600, !noControls);

  const isMobile = useScreenWidth() <= 768;

  const descriptionFilterId = useRef<string>(v4()).current;

  const onCancel = () => {
    onCancelProp?.();
    setAndFilters(filters.filter((filter) => !filter.isOr && !nonUItypes.includes(filter.type)));
    setOrFilters(filters.filter((filter) => filter.isOr && !nonUItypes.includes(filter.type)));
  };

  const onNewAndFilter = () => {
    setAndFilters([...andFilters, { id: v4(), type: "tag", isOr: false, operand: "includes" }]);
  };

  const onNewOrFilter = () => {
    setOrFilters([...orFilters, { id: v4(), type: "tag", isOr: true, operand: "includes" }]);
  };

  const onDeleteAndFilter = (id: string) => {
    setAndFilters(andFilters.filter((f) => f.id !== id));
  };

  const onDeleteOrFilter = (id: string) => {
    setOrFilters(orFilters.filter((f) => f.id !== id));
  };

  const onChangeAndFilter = (id: string, filter: ConditionRowFilter) => {
    setAndFilters(aggregateErrors(andFilters.map((f) => (f.id === id ? filter : f))));
  };

  const onChangeOrFilter = (id: string, filter: ConditionRowFilter) => {
    setOrFilters(aggregateErrors(orFilters.map((f) => (f.id === id ? filter : f))));
  };

  const onSave = () => {
    const descriptionFilter: ConditionRowFilter = {
      value: searchValue.trim(),
      id: descriptionFilterId,
      type: "description",
      operand: "is",
      isOr: false
    };
    onSaveProp([...andFilters, ...orFilters, ...(descriptionFilter.value ? [descriptionFilter] : [])]);
  };

  const onSaveBasic = () => {
    const descriptionFilter: ConditionRowFilter = {
      value: searchValue.trim(),
      id: descriptionFilterId,
      type: "description",
      operand: "is",
      isOr: false
    };
    const locationFilters = simpleFilters.filter((f) => f.type === "location");
    const stagesFilters = simpleFilters.filter((f) => f.type === "financialStage");
    const filters: ConditionRowFilter[] = [];
    if (descriptionFilter.value) filters.push(descriptionFilter);
    if (locationFilters.length)
      filters.push({
        type: "group",
        id: "basicLocationFiltersGroup",
        value: "",
        operand: "is",
        isOr: false,
        filters: locationFilters
      });
    if (stagesFilters.length)
      filters.push({
        type: "group",
        id: "basicStagesFiltersGroup",
        value: "",
        operand: "is",
        isOr: false,
        filters: stagesFilters
      });
    onSaveProp(filters);
  };

  useEffect(() => {
    const descFilter = filters.find((f) => f.type === "description")?.value;
    setSearchValue(descFilter || "");
  }, [filters]);

  useEffect(() => {
    if (!useAdvancedFilters) return;
    if (!noControls) return;
    if (!canSave(andFilters) || !canSave(orFilters)) return;
    const descriptionFilter: ConditionRowFilter = {
      value: searchValue.trim(),
      id: descriptionFilterId,
      type: "description",
      operand: "is",
      isOr: false
    };
    if (
      JSON.stringify(filters) !==
      JSON.stringify([...andFilters, ...orFilters, ...(descriptionFilter.value ? [descriptionFilter] : [])])
    ) {
      onSave();
    }
  }, [andFilters, orFilters, useAdvancedFilters, debouncedSearchValue]);

  useEffect(() => {
    if (useAdvancedFilters) return;
    if (!noControls) return;
    if (!canSave(simpleFilters)) return;
    const descriptionFilter: ConditionRowFilter = {
      value: searchValue.trim(),
      id: descriptionFilterId,
      type: "description",
      operand: "is",
      isOr: false
    };

    if (
      JSON.stringify(filters) !==
      JSON.stringify([...simpleFilters, ...(descriptionFilter.value ? [descriptionFilter] : [])])
    ) {
      onSaveBasic();
    }
  }, [useAdvancedFilters, simpleFilters, debouncedSearchValue]);

  useEffect(() => {
    onFiltersComplexityTypeChange?.(hasAdvancedFilters(filters) ? "advanced" : "basic");
  }, []);

  return (
    <>
      <div className={styles.contentContainer}>
        {!isMobile && (
          <div className={styles.titleContainer}>
            <Typography variant="heading-7" color="greyscale900" className={styles.title}>
              Filters
            </Typography>
            <NormalButton
              size="small"
              onClick={() => {
                setAndFilters([]);
                setOrFilters([]);
                setSimpleFilters([]);
                setSearchValue("");
                onSaveProp([]);
              }}
              variant="tertiary"
              color="greyscale600"
              className={styles.clearAllButton}
              id={prefixId + "filtersV2-clearAllButton"}
            >
              Clear All
            </NormalButton>
          </div>
        )}

        {!noSearch && (
          <div className={styles.conditionsGroup}>
            <Typography variant="label-4" color="greyscale900" className={styles.groupTitle}>
              <AiIcon width={20} height={20} className={styles.aiIcon} /> Scout by description
            </Typography>
            <SearchInput
              isTextarea
              value={searchValue}
              onChange={(e) => setSearchValue(e)}
              placeholder="e.g., AI-powered fintech startups building fraud detection tools"
              className={styles.searchInput}
              inputId={prefixId + "filtersV2-descriptionInput"}
            />
          </div>
        )}

        <div className={styles.tabsContainer}>
          <Tabs
            tabs={[
              {
                label: "Basic",
                key: "basic"
              },
              {
                label: "Advanced",
                key: "advanced"
              }
            ]}
            activeTab={useAdvancedFilters ? "advanced" : "basic"}
            onTabChange={(tab) => {
              setUseAdvancedFilters(tab === "advanced");
              onFiltersComplexityTypeChange?.(tab);
            }}
          />
        </div>

        {useAdvancedFilters && (
          <>
            <div className={styles.conditionsGroup}>
              <Typography variant="label-4" color="greyscale700" className={styles.groupTitle}>
                Match <b>ALL</b> of these conditions
              </Typography>
              {andFilters.map((filter, index) => (
                <ConditionRow
                  label={index === 0 ? "Where" : "And"}
                  linked={index < andFilters.length - 1}
                  key={filter.id}
                  {...filter}
                  onDelete={() => onDeleteAndFilter(filter.id)}
                  onConditionChange={(v) => onChangeAndFilter(filter.id, v)}
                  options={options}
                  prefixId={prefixId}
                  wrap={wrap}
                />
              ))}
              <NormalButton
                size="medium"
                leftIcon
                className={styles.addConditionButton}
                variant="tertiary"
                onClick={onNewAndFilter}
                disabled={andFilters.length + orFilters.length >= FILTERS_LIMIT}
                id={prefixId + "filtersV2-matchAll-addConditionButton"}
              >
                Add Condition
              </NormalButton>
            </div>
            <Divider />

            <div className={styles.conditionsGroup}>
              <Typography variant="label-4" color="greyscale700" className={styles.groupTitle}>
                And match <b>ANY</b> of these conditions
              </Typography>
              {orFilters.map((filter, index) => (
                <ConditionRow
                  prefixId={prefixId}
                  linked={index < orFilters.length - 1}
                  label={index === 0 ? "Where" : "Or"}
                  key={filter.id}
                  {...filter}
                  onDelete={() => onDeleteOrFilter(filter.id)}
                  onConditionChange={(v) => onChangeOrFilter(filter.id, v)}
                  options={options}
                  wrap={wrap}
                />
              ))}
              <NormalButton
                size="medium"
                leftIcon
                className={styles.addConditionButton}
                variant="tertiary"
                onClick={onNewOrFilter}
                disabled={andFilters.length + orFilters.length >= FILTERS_LIMIT}
                id={prefixId + "filtersV2-matchAny-addConditionButton"}
              >
                Add Condition
              </NormalButton>
            </div>
            <Divider />
          </>
        )}

        {!useAdvancedFilters && <BasicFilters filters={simpleFilters} setFilters={setSimpleFilters} />}
      </div>

      {!noControls && (
        <>
          <Divider className={styles.noMarginBottom} />
          <div className={styles.actions}>
            <NormalButton variant="secondary" size="large" onClick={onCancel} id={prefixId + "filtersV2-cancelButton"}>
              Cancel
            </NormalButton>
            <NormalButton
              size="large"
              onClick={onSave}
              disabled={!canSave(andFilters) || !canSave(orFilters)}
              id={prefixId + "filtersV2-saveButton"}
            >
              Apply
            </NormalButton>
          </div>
        </>
      )}
    </>
  );
}

export function ModalFilters({ anchorEl, fullWidth, isOpen, ...rest }: TFiltersModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useScreenWidth() <= 768;

  useOnClickOutside(
    ref,
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      rest.onCancel?.();
    },
    isOpen
  );

  if (isMobile) {
    return (
      <Drawer2 isOpen={isOpen} position="bottom" onClose={rest.onCancel} title="Filters">
        <Filters {...rest} />
      </Drawer2>
    );
  }

  return (
    <div
      ref={ref}
      className={styles.container}
      style={{
        ...getPositionBasedOnAnchor(anchorEl),
        width: fullWidth ? "100%" : "fit-content",
        display: isOpen ? "flex" : "none"
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Filters {...rest} />
    </div>
  );
}
